import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    // A media-gallery node can be retained and reconnected by morph. AbortSignal
    // instances cannot be reused after disconnect, so renew it before rebinding.
    if (this.#controller.signal.aborted) this.#controller = new AbortController();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (this.closest('quick-add-dialog')) {
      this.addEventListener('click', this.#handleQuickAddAssignedGalleryClick, { signal });
      this.addEventListener('keydown', this.#handleQuickAddAssignedGalleryKeydown, { signal });
      this.addEventListener('pointerover', this.#preloadQuickAddAssignedGalleryImage, { signal });
      this.addEventListener('focusin', this.#preloadQuickAddAssignedGalleryImage, { signal });
      queueMicrotask(() => this.#mountQuickAddAssignedGallery());
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#quickAddMediaController?.abort();
    if (this.#variantMediaTimer) window.clearTimeout(this.#variantMediaTimer);
  }

  /** @type {number | undefined} */
  #variantMediaTimer;

  /** @type {AbortController | undefined} */
  #quickAddMediaController;

  /** Monotonic guard so only the newest variant response may move product media. */
  #variantMediaRequest = 0;

  /** @type {Map<string, Promise<boolean>>} */
  #extraGalleryImagePreloads = new Map();

  /** Monotonic guard so a late full-size image cannot replace a newer selection. */
  #extraGalleryImageRequest = 0;

  /**
   * Selects media in Quick View's intentionally stacked single-media surface.
   * The shared slideshow normally derives aria-hidden from horizontal scroll
   * positions; Quick View overlays every slide at the same coordinates, so it
   * needs an explicit active slide handoff after the new image is ready.
   *
   * @param {HTMLElement} targetSlide
   */
  #selectQuickAddMedia(targetSlide) {
    const slideshow = this.slideshow;
    const slides = slideshow?.refs.slides ?? [];
    if (!slideshow || !slides.includes(targetSlide)) return;

    this.#quickAddMediaController?.abort();
    const controller = new AbortController();
    this.#quickAddMediaController = controller;
    const { signal } = controller;

    const commit = () => {
      if (signal.aborted) return;

      targetSlide.removeAttribute('hidden');
      targetSlide.removeAttribute('reveal');

      for (const slide of slides) {
        slide.setAttribute('aria-hidden', String(slide !== targetSlide));
      }

      const targetIndex = slideshow.slides?.indexOf(targetSlide) ?? -1;
      if (targetIndex >= 0) {
        slideshow.current = targetIndex;
        this.#mountQuickAddAssignedGallery();
      }

      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        targetSlide.animate([{ opacity: 0.72 }, { opacity: 1 }], {
          duration: 160,
          easing: 'ease-out',
        });
      }
    };

    const image = targetSlide.querySelector('img');
    if (!(image instanceof HTMLImageElement) || image.complete) {
      commit();
      return;
    }

    // Keep the old slide visible while Shopify's responsive target image loads.
    image.loading = 'eager';
    image.addEventListener('load', commit, { once: true, signal });
    image.addEventListener('error', commit, { once: true, signal });
  }

  /**
   * Centers resolved variant media in the unobscured PDP gallery area. On
   * desktop, the details panel overlays the rail from the right, so its actual
   * intersection is excluded from the usable viewport. Direct scrolling avoids
   * the distant-slide DOM reorder and makes rapid selections latest-wins.
   *
   * @param {string} mediaId
   */
  #selectProductMedia(mediaId) {
    const slideshow = this.slideshow;
    const scroller = slideshow?.refs.scroller;
    if (!slideshow || !(scroller instanceof HTMLElement)) return;

    let viewportEndInset = 0;

    if (window.matchMedia('(min-width: 990px)').matches) {
      const details = this.closest('.product-information__grid')?.querySelector('.product-details');

      if (details instanceof HTMLElement) {
        const scrollerRect = scroller.getBoundingClientRect();
        const detailsRect = details.getBoundingClientRect();
        const overlapsVertically = detailsRect.bottom > scrollerRect.top && detailsRect.top < scrollerRect.bottom;

        if (overlapsVertically && detailsRect.left > scrollerRect.left) {
          viewportEndInset = Math.max(0, scrollerRect.right - detailsRect.left);
        }
      }
    }

    slideshow.select(
      { id: mediaId },
      undefined,
      { animate: true, direct: true, alignment: 'center', viewportEndInset }
    );
  }

  /**
   * Product pages already own the authoritative `indri-variant-extra-gallery`
   * mapping. In Quick View it initially renders in the hidden additional
   * content area, so move that exact component under the main media instead
   * of treating every product media item as an extra variant image.
   */
  #mountQuickAddAssignedGallery() {
    const dialog = this.closest('quick-add-dialog');
    const assignedGallery = dialog?.querySelector('indri-variant-extra-gallery');
    const track = assignedGallery?.querySelector('[data-extra-images-track]');

    if (!assignedGallery || !track) return;

    assignedGallery.hidden = assignedGallery.hidden || track.childElementCount === 0;
    if (!assignedGallery.hidden) this.append(assignedGallery);
  }

  /** @param {Document} html */
  #syncQuickAddAssignedGallery(html) {
    const dialog = this.closest('quick-add-dialog');
    const assignedGallery = dialog?.querySelector('indri-variant-extra-gallery');
    const replacementGallery = html.querySelector('indri-variant-extra-gallery');

    if (!assignedGallery) return;

    const track = assignedGallery.querySelector('[data-extra-images-track]');
    const replacementTrack = replacementGallery?.querySelector('[data-extra-images-track]');
    if (!track || !replacementTrack) {
      assignedGallery.hidden = true;
      return;
    }

    track.replaceChildren(...[...replacementTrack.children].map((child) => child.cloneNode(true)));
    assignedGallery.hidden = replacementGallery.hidden || track.childElementCount === 0;
    this.#mountQuickAddAssignedGallery();
  }

  /**
   * Starts fetching a full-size assigned-gallery image without delaying the
   * thumbnail interaction. Hover and keyboard focus usually complete this
   * before the customer activates the thumbnail; on touch, the modal still
   * opens immediately with its small preview.
   *
   * @param {Element} trigger
   * @returns {Promise<boolean> | null}
   */
  #preloadQuickAddAssignedGallerySource(trigger) {
    const source = trigger.getAttribute('data-full-src');
    if (!source) return null;

    const existing = this.#extraGalleryImagePreloads.get(source);
    if (existing) return existing;

    const preload = new Promise((resolve) => {
      const image = new Image();
      const settle = () => resolve(image.naturalWidth > 0);

      image.addEventListener('load', settle, { once: true });
      image.addEventListener('error', settle, { once: true });
      image.src = source;

      if (image.complete) settle();
    });

    this.#extraGalleryImagePreloads.set(source, preload);
    return preload;
  }

  /** @param {Event} event */
  #preloadQuickAddAssignedGalleryImage = (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-extra-image-trigger]');
    if (!trigger || !this.contains(trigger)) return;

    this.#preloadQuickAddAssignedGallerySource(trigger);
  };

  /**
   * The enlarged assigned-gallery image is a modal nested inside Quick View.
   * Escape must close that topmost modal only; otherwise it bubbles into the
   * parent DialogComponent and starts closing Quick View underneath it.
   *
   * @param {KeyboardEvent} event
   */
  #handleQuickAddAssignedGalleryKeydown = (event) => {
    if (event.key !== 'Escape') return;

    const dialog = this.querySelector('indri-variant-extra-gallery [data-extra-images-dialog]');
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;

    event.preventDefault();
    event.stopPropagation();
    dialog.close();
  };

  /** @param {MouseEvent} event */
  #handleQuickAddAssignedGalleryClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const assignedGallery = event.target.closest('indri-variant-extra-gallery');
    if (!assignedGallery || !this.contains(assignedGallery)) return;

    const closeButton = event.target.closest('[data-extra-images-close]');
    const dialog = assignedGallery.querySelector('[data-extra-images-dialog]');
    if (closeButton || event.target === dialog) {
      dialog?.close();
      return;
    }

    const trigger = event.target.closest('[data-extra-image-trigger]');
    if (!trigger || !(dialog instanceof HTMLDialogElement)) return;

    const image = assignedGallery.querySelector('[data-extra-images-dialog-image]');
    const source = trigger.getAttribute('data-full-src');
    if (!(image instanceof HTMLImageElement) || !source) return;

    event.preventDefault();
    const preview = trigger.querySelector('img');
    const previewSource = preview instanceof HTMLImageElement ? preview.currentSrc || preview.src : '';
    const imageRequest = ++this.#extraGalleryImageRequest;

    // Do not leave the zoom dialog blank while the inspect-quality image is
    // loading. The visible thumbnail gives immediate feedback, then the
    // already-warmed full image replaces it without changing the dialog size.
    image.src = previewSource || source;
    image.alt = trigger.getAttribute('data-alt') || '';
    if (!dialog.open) dialog.showModal();

    const preload = this.#preloadQuickAddAssignedGallerySource(trigger);
    preload?.then((loaded) => {
      if (!loaded || imageRequest !== this.#extraGalleryImageRequest || !dialog.open) return;

      image.src = source;
      image.decode?.().catch(() => {
        // The browser can still display the successfully fetched image when
        // decoding is deferred or unsupported.
      });
    });
  };

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const mediaRequest = ++this.#variantMediaRequest;

    if (this.#variantMediaTimer) {
      window.clearTimeout(this.#variantMediaTimer);
      this.#variantMediaTimer = undefined;
    }

    event.promise
      .then(({ detail }) => {
        if (mediaRequest !== this.#variantMediaRequest) return;
        if (!detail?.html) return;

        const { html, resource } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        const resolvedMediaId = resource?.featured_media?.id || resource?.featured_image?.id;
        const sameProduct = newMediaGallery.dataset.productId === this.dataset.productId;
        const isQuickAdd = this.closest('quick-add-dialog') !== null;
        const currentMedia = resolvedMediaId
          ? this.querySelector(`slideshow-slide[slide-id="${CSS.escape(String(resolvedMediaId))}"]`)
          : null;

        if (isQuickAdd) this.#syncQuickAddAssignedGallery(html);

        // All variant media for the current product already live in this rail.
        // Move the existing Shopify slideshow instead of replacing the gallery,
        // so the relationship between neighboring media remains spatial.
        if (sameProduct) {
          if (currentMedia) {
            if (isQuickAdd) {
              this.#selectQuickAddMedia(currentMedia);
              return;
            }

            const selectResolvedMedia = () => {
              if (mediaRequest !== this.#variantMediaRequest) return;
              this.#variantMediaTimer = undefined;
              this.#selectProductMedia(String(resolvedMediaId));
            };

            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
              selectResolvedMedia();
            } else {
              // Selection state, price, and availability update immediately. The
              // short pause lets the subsequent gallery movement read spatially.
              this.#variantMediaTimer = window.setTimeout(selectResolvedMedia, 90);
            }
          }
          return;
        }

        // Combined listings can introduce a genuinely different media dataset.
        // In that case the server-rendered gallery remains authoritative.
        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
